/**
 * SAWelcomeGrid
 * Default grid view with product images and search term suggestion tiles.
 * Displayed when there are no messages (initial state).
 * Reusable — used by both panel and page.
 */

import { MdSearch } from 'react-icons/md';
import type { SATheme } from '../../types/stylingAssistant.types';

// ─── Grid Data Types ─────────────────────────────────────────────────────────

type ProductItem = { type: 'product'; image: string };
type SearchTermItem = { type: 'searchTerm'; searchTermIndex: number };
type GridItem = ProductItem | SearchTermItem;
interface GridRow {
  items: GridItem[];
}
interface GridData {
  searchTerms: string[];
  rows: GridRow[];
}

// ─── Static Grid Data ────────────────────────────────────────────────────────

const initialGridData: GridData = {
  searchTerms: [
    'I am looking for elegant cocktail party dresses for a night event',
    'Looking for structured handbags in a modern aesthetic',
    'Looking for blue dresses with a refined silhouette',
    'Una chaqueta de moto inspirada en la motocicleta con un diseño estructurado y vanguardista',
    'I am looking for women\'s tops without buttons',
    'I am looking for winter outfits',
    'I am looking for men\'s tops between 50 and 150 dollars',
  ],
  rows: [
    {
      items: [
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/qh5Pu5hf4wA2jWMbw5XhjlMlTPD7dVJx3xlYlqPyzo.jpg?v=1747409696&width=900' },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/f_TLe9ibr1iDga06rl8EzKyIOLKinpMm7H3I2UyIy38.jpg?v=1728944729&width=900' },
        { type: 'searchTerm', searchTermIndex: 0 },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/t-BIJc-hXLeov96TETNMsgi6icgzCX5nOkpqMNy-qlg.jpg?v=1752247564&width=900' },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/wk1PyFr5azG0tw73bmtuH-aJUa-6lI317ovf-Tz8CJ4.jpg?v=1752247711&width=900' },
      ],
    },
    {
      items: [
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1811-LCL419X_1N001_OS_A?w=1280' },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L0810-LCL120G_1G003_OS_A?w=1280' },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1835-ISA073X_1N001_OS_A?w=1280' },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1755-LCL095G_1N001_OS_A?w=1280' },
        { type: 'searchTerm', searchTermIndex: 1 },
      ],
    },
    {
      items: [
        { type: 'searchTerm', searchTermIndex: 2 },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/HKoHNn6otgYUbe8pXtD_Dwaf3m3DOeYUuf2NTMgomVk.jpg?v=1752242447&width=900' },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/fZbnaFJKCM8wtF9jMrySVbsBbfnjUhJr9GZSUe8nT6o.jpg?v=1746758057&width=900' },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/6kKZ11ih3ryi5OOAfTMqIniQXTlJIhVLJLYAztM6wto.jpg?v=1747431630&width=900' },
        { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/UB3wT7ZGvdWHOJsOGgfJJBY3TAQe7tRxIcjHj0XVtMw.jpg?v=1752076970&width=900' },
      ],
    },
    {
      items: [
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/fabulous-furs-ocean-fox-faux-fur-happy-hour-jacket__09303_grande.jpg?v=1727293846' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/fabulous-furs-olive-faux-fur-le-mink-jacket__75581_grande.jpg?v=1725470552' },
        { type: 'searchTerm', searchTermIndex: 5 },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/zay1vpyl1pexucv8jw9p_grande.jpg?v=1724430120' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/cslcqnrycvyqqoikfjpg_grande.jpg?v=1728312050' },
      ],
    },
    {
      items: [
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/CSL764-LEX314_LB999_OS_A?w=1280' },
        { type: 'searchTerm', searchTermIndex: 3 },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LBS010-LMN014S24_LB999_OS_A?w=1280' },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LBS039-LMS002S23_HB425_OS_A?w=1280' },
        { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LXR004-LMN014S24_LB999_OS_A?w=1280' },
      ],
    },
    {
      items: [
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/wlfcolszscgmlg9bcmqp_grande.jpg?v=1715963946' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/ripley-rader-tops-1-xs-navy-navy-ponte-knit-tank-top-39228615917788_grande.jpg?v=1719417494' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/x9cs2j7eie3xl0ejh3ur_grande.png?v=1718976943' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/a4d9rrpotsfru9cktjca_grande.jpg?v=1722360866' },
        { type: 'searchTerm', searchTermIndex: 4 },
      ],
    },
    {
      items: [
        { type: 'searchTerm', searchTermIndex: 6 },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/lu9rgsx6f2xwayucwfb5_grande.jpg?v=1710257286' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/3a7dd10eb93511b766c567fba5c91997_grande.jpg?v=1695156423' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/oveka6or2lb69bggmjmr_grande.jpg?v=1715188300' },
        { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/yglt9ktkwhzbgka30hj8_grande.jpg?v=1724869504' },
      ],
    },
  ],
};

// ─── Component ───────────────────────────────────────────────────────────────

interface SAWelcomeGridProps {
  theme: SATheme;
  onSuggestionClick: (suggestion: string) => void;
}

export default function SAWelcomeGrid({
  theme,
  onSuggestionClick,
}: SAWelcomeGridProps) {
  return (
    <div className="px-4 pb-4">
      <div className="space-y-3">
        {initialGridData.rows.map((row, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="grid gap-3"
            style={{
              gridTemplateColumns: row.items
                .map((item) =>
                  item.type === 'searchTerm'
                    ? 'minmax(0, 2fr)'
                    : 'minmax(0, 1fr)'
                )
                .join(' '),
            }}
          >
            {row.items.map((item, itemIndex) => {
              if (item.type === 'searchTerm' && 'searchTermIndex' in item) {
                const searchTerm = initialGridData.searchTerms[item.searchTermIndex];
                return (
                  <button
                    key={`search-${rowIndex}-${itemIndex}`}
                    onClick={() => onSuggestionClick(searchTerm)}
                    className="p-4 rounded-xl transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-2 hover:scale-[1.02] group"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      minHeight: '140px',
                    }}
                  >
                    <MdSearch
                      size={22}
                      className="text-gray-500 group-hover:text-[#EE4423] transition-colors"
                    />
                    <p
                      className="text-xs font-medium leading-snug px-1"
                      style={{ color: theme.text }}
                    >
                      {searchTerm}
                    </p>
                  </button>
                );
              } else {
                return (
                  <div
                    key={`product-${rowIndex}-${itemIndex}`}
                    className="relative"
                  >
                    <div
                      className="w-full rounded-xl overflow-hidden hover:shadow-lg transition-all"
                      style={{
                        backgroundColor: '#1a1a1a',
                        height: '140px',
                      }}
                    >
                      <img
                        src={(item as ProductItem).image}
                        alt="Product"
                        className="w-full h-full object-top bg-white object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              }
            })}
          </div>
        ))}
      </div>

      {/* Footer Disclaimer */}
      <div className="text-center px-3 mt-3">
        <p className="text-[10px] leading-tight" style={{ color: theme.textMuted }}>
          By submitting a search via the virtual style assistant, you agree to
          the information being processed accordingly to our Terms & Conditions
          and Privacy Notice
        </p>
      </div>
    </div>
  );
}
